import { createContext, useContext, useState, useMemo, useCallback } from 'react';

import { useBreakpointValue } from '@vimeo/bokeh';

// Drawer state for mobile where these modules are rendered within a Drawer modal
type DrawerType =
  | 'COMMENTS'
  | 'COMMENTS_SVV'
  | 'DOWNLOAD'
  | 'VIMEO_AI'
  | 'TRANSCRIPT'
  | 'CHAPTERS'
  | 'LIVE_TOOLS'
  | 'LIKES'
  | '';

// State for desktop side modules
export type ModuleType = 'VIMEO_AI' | 'TRANSCRIPT' | 'CHAPTERS' | 'LIVE_TOOLS' | '';
type DeeplinkableModuleType = Exclude<ModuleType, ''>;

export type GridType =
  | 'STACKED'
  | 'STACKED_NO_COMMENTS'
  | 'SIDE_BY_SIDE'
  | 'SIDE_BY_SIDE_WITH_SIDE_MODULE'
  | 'SIDE_BY_SIDE_WITH_SIDE_MODULE_NO_COMMENTS';

type LayoutContextType = {
  isMobileDrawerOpen: boolean;
  isSideModuleOpen: boolean;
  isSideBySideActive: boolean;
  activeMobileDrawer: DrawerType;
  activeSideModule: ModuleType;
  toggleSideBySideView: () => void;
  setActiveMobileDrawer: (drawer: DrawerType) => void;
  setActiveSideModule: (drawer: ModuleType) => void;
  onClose: (() => void) | (() => void) | undefined;
  onDeeplinkDrawerModuleChange: (moduleVisibility: Record<DeeplinkableModuleType, boolean>) => void;
  onActiveDrawerModuleChange: (type: DrawerType | ModuleType) => void;
  isShowcaseLayout?: boolean;
};

const defaultLayoutState: LayoutContextType = {
  activeMobileDrawer: '',
  activeSideModule: '',
  isMobileDrawerOpen: false,
  isSideModuleOpen: false,
  isSideBySideActive: false,
  toggleSideBySideView: () => console.warn('LayoutProvider not found'),
  setActiveMobileDrawer: () => console.warn('LayoutProvider not found'),
  setActiveSideModule: () => console.warn('LayoutProvider not found'),
  onClose: () => console.warn('LayoutProvider not found'),
  onDeeplinkDrawerModuleChange: () => console.warn('LayoutProvider not found'),
  onActiveDrawerModuleChange: () => console.warn('LayoutProvider not found'),
  isShowcaseLayout: false,
};

const LayoutContext = createContext<LayoutContextType>(defaultLayoutState);

const getSideBySideInitialState = () => {
  if (typeof window === 'undefined') return false;

  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  return hashParams.has('comments');
};

/**
 * A hook to manage all state that influences the layout of the viewer home page
 * isSideBySideActive - Whether the side by side view is active, via sideBySide or side module being open
 * activeSideModule - The currently active side module for desktop
 * activeMobileDrawer - The currently active drawer for mobile
 */
export const LayoutProvider = ({
  children,
  isShowcaseLayout,
}: React.PropsWithChildren<{ isShowcaseLayout?: boolean }>) => {
  const [activeMobileDrawer, setActiveMobileDrawer] = useState<DrawerType>('');
  const [activeSideModule, setActiveSideModule] = useState<ModuleType>('');

  const [sideBySide, setSideBySide] = useState(getSideBySideInitialState());

  const isSideModuleOpen = activeSideModule !== '';
  const isSideBySideActive = sideBySide || isSideModuleOpen;

  const toggleSideBySideView = () => {
    setSideBySide((prev) => !prev);
  };

  const isMobileView = useBreakpointValue({ base: true, md: false }, { fallback: 'md' });

  const onActiveDrawerModuleChange = useCallback(
    (type: DrawerType | ModuleType) => {
      if (isMobileView) {
        setActiveMobileDrawer(type);
        return;
      }

      // Type guard for side by side module
      if (
        type !== 'COMMENTS' &&
        type !== 'COMMENTS_SVV' &&
        type !== 'DOWNLOAD' &&
        type !== 'LIKES'
      ) {
        setActiveSideModule(type);
      }
    },
    [isMobileView],
  );

  // Close the drawer or side module based on the current breakpoint
  const onClose = useBreakpointValue({
    base: () => setActiveMobileDrawer(''),
    md: () => setActiveSideModule(''),
  });

  // Deeplink search params will impact layout default active drawer module
  const deeplinkDrawerModulePriorities = useMemo(() => {
    if (typeof window === 'undefined') return [];

    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const searchParams = new URLSearchParams(window.location.search);

    const modules: DeeplinkableModuleType[] = [];

    if (searchParams.has('tq') || searchParams.get('autotranscript') === '1') {
      modules.push('TRANSCRIPT');
    }

    if (hashParams.has('chapter')) {
      modules.push('CHAPTERS');
    }

    if (searchParams.get('open_ai') === 'true') {
      modules.push('VIMEO_AI');
    }

    return modules;
  }, []);

  const onDeeplinkDrawerModuleChange = useCallback(
    (moduleVisibility: Record<DeeplinkableModuleType, boolean>) => {
      if (deeplinkDrawerModulePriorities.length === 0) return;

      for (const type of deeplinkDrawerModulePriorities) {
        if (moduleVisibility[type]) {
          onActiveDrawerModuleChange(type);
          break;
        }
      }
    },
    [deeplinkDrawerModulePriorities, onActiveDrawerModuleChange],
  );

  return (
    <LayoutContext.Provider
      value={{
        isMobileDrawerOpen: !!activeMobileDrawer,
        isSideModuleOpen,
        isSideBySideActive,
        toggleSideBySideView,
        setActiveMobileDrawer,
        activeMobileDrawer,
        setActiveSideModule,
        activeSideModule,
        onClose,
        onDeeplinkDrawerModuleChange,
        isShowcaseLayout,
        onActiveDrawerModuleChange,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const context = useContext(LayoutContext);

  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }

  return context;
};
